///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { cube } from "@stovokor/math/cube"
import { sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * Unit UV sphere used as the ray-march proxy for the aurora volume
 * (aurora_volume.glsl scales it to the top of the layer). Fine enough that
 * the polygonal surface stays within ~8 km of the true sphere, so the
 * proxy covers every pixel the layer can occupy right out to the limb.
 * Bounds are declared at the shell radius so the node is frustum-culled
 * correctly from any camera, including one under the layer looking up.
 */
export class AuroraShellGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, shellRadiusKm: number, latSegments: number = 32, lonSegments: number = 64) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])

		for(let lat = 0; lat <= latSegments; lat++) {
			const phi = (lat / latSegments) * Math.PI - Math.PI / 2
			const y = Math.sin(phi)
			const ringRadius = Math.cos(phi)
			for(let lon = 0; lon <= lonSegments; lon++) {
				const theta = (lon / lonSegments) * Math.PI * 2
				this.position.add(Math.cos(theta) * ringRadius, y, Math.sin(theta) * ringRadius)
			}
		}

		const stride = lonSegments + 1
		for(let lat = 0; lat < latSegments; lat++) {
			for(let lon = 0; lon < lonSegments; lon++) {
				const a = lat * stride + lon
				const b = a + stride
				this.indexBuffer.quad(a, b, b + 1, a + 1)
			}
		}

		this.bounds = new cube(-shellRadiusKm, shellRadiusKm)
		this.boundingSphere = new sphere(shellRadiusKm)
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
